import _ from 'lodash';

Mobi2Go._class = Mobi2Go._class || {};
const { serverRequest } = Mobi2Go.app;
let instance = -1;
let password_to_verify = null;
const VALID_PASSWORD_TTL = 1000 * 60 * 30;

/**-----------------------------------------------------------------------------
 * Represents an instance of a customer
 */
var Customer = (Mobi2Go._class.Customer = function(properties) {
    if (!(this instanceof Customer)) {
        return new Customer(properties);
    }

    this.init(properties);
});

_.extend(Customer.prototype, {
    /**-------------------------------------------------------------------------
     */
    init(properties) {
        this.id = null;
        this.email = null;
        this.name = '';
        this.phone_number = '';
        this.loyalty_id = '';
        this.is_guest = false;
        this.is_subscriber = false;
        this.addresses = Mobi2Go._class.Common.Collection([]);
        this.rewards = Mobi2Go._class.Common.Collection([]);
        this.loyalty = {};
        this.credit_balance = Mobi2Go._class.Common.Price(0.0);
        this.date_of_birth = null;
        this.gender = null;
        this.dietary_preference = [];
        this.favourite_location = null;
        this.post_code = null;
        this.language = null;
        this.verified_at = null;

        instance++;
        Mobi2Go.app.mixinEvents(`CustomerInstance#${instance}`, this);

        if (properties) {
            this.set(properties);
        }
    },

    /**-------------------------------------------------------------------------
     */
    get() {
        return _.pick(this, [
            'id',
            'email',
            'name',
            'phone_number',
            'is_guest',
            'is_subscriber',
            'is_active',
            'rewards',
            'loyalty',
            'credit_balance',
            'date_of_birth',
            'gender',
            'dietary_preference',
            'favourite_location',
            'post_code',
            'language',
            'verfied_at',
        ]);
    },

    /**-------------------------------------------------------------------------
     */
    set(new_properties) {
        if (new_properties.id !== undefined) {
            this.id = new_properties.id;
        }
        if (new_properties.email !== undefined) {
            this.email = new_properties.email;
        }
        if (new_properties.name !== undefined) {
            this.setName(new_properties.name);
        }
        if (new_properties.phone_number !== undefined) {
            this.setPhoneNumber(new_properties.phone_number);
        }
        if (new_properties.loyalty_id !== undefined) {
            this.setLoyaltyId(new_properties.loyalty_id);
        }
        if (new_properties.is_guest !== undefined) {
            this.is_guest = !!new_properties.is_guest;
        }
        if (new_properties.is_subscriber !== undefined) {
            this.is_subscriber = !!new_properties.is_subscriber;
        }
        if (new_properties.addresses !== undefined) {
            this.setAddresses(new_properties.addresses);
        }
        if (new_properties.rewards !== undefined) {
            this.setRewards(new_properties.rewards);
        }
        if (new_properties.loyalty !== undefined) {
            this.setLoyalty(new_properties.loyalty);
        }
        if (new_properties.password !== undefined) {
            password_to_verify = new_properties.password;
            this.isPasswordProvided.is_valid = true;
        }
        if (new_properties.is_active !== undefined) {
            this.is_active = true;
        }
        if (new_properties.credit_balance !== undefined) {
            this.setCreditBalance(new_properties.credit_balance);
        }

        if (new_properties.date_of_birth !== undefined) {
            this.setDateOfBirth(new_properties.date_of_birth);
        }

        if (new_properties.gender !== undefined) {
            this.setGender(new_properties.gender);
        }

        if (new_properties.dietary_preference !== undefined) {
            this.setDietaryPreference(new_properties.dietary_preference);
        }

        if (new_properties.favourite_location !== undefined) {
            this.setFavouriteLocation(new_properties.favourite_location);
        }

        if (new_properties.post_code !== undefined) {
            this.setPostCode(new_properties.post_code);
        }

        if (new_properties.language !== undefined) {
            this.setLanguage(new_properties.language);
        }

        if (new_properties.verified_at !== undefined) {
            this.setVerifiedAt(new_properties.verified_at);
        }

        return this;
    },

    /**-------------------------------------------------------------------------
     */
    setName(new_name) {
        new_name = sanitiseName(new_name).trim();
        if (!new_name) {
            throw new Error('Name cannot be blank');
        }

        this.name = new_name;
    },

    /**-------------------------------------------------------------------------
     */
    getFirstName() {
        return this.name.split(' ')[0];
    },

    /**-------------------------------------------------------------------------
     */
    getLastName() {
        const names = this.name.split(' ');
        return names[1] !== undefined ? names[1] : '';
    },

    /**-------------------------------------------------------------------------
     */
    getPassword() {
        return password_to_verify;
    },

    /**-------------------------------------------------------------------------
     */
    setPhoneNumber(new_phone_number) {
        if (Mobi2Go.headoffice.customer_phone_number_validation_enabled) {
            this.phone_number = new_phone_number;
        } else {
            if (new_phone_number) {
                if (new_phone_number.length < 5) {
                    throw new Error(
                        'Phone number must be at least 5 characters long'
                    );
                }

                this.phone_number = sanitiseNumber(new_phone_number);
            }
        }
    },

    /**-------------------------------------------------------------------------
     */
    setLoyaltyId(loyalty_id) {
        this.loyalty_id = sanitiseName(loyalty_id).trim();
    },

    /**-------------------------------------------------------------------------
     */
    getVerifiedAt() {
        return this.verified_at;
    },

    /**-------------------------------------------------------------------------
     */
    setVerifiedAt(verified_at) {
        if (!_.isEqual(verified_at, this.verified_at)) {
            this.verified_at = verified_at;
            this.trigger('updateVerifiedAt', this.verified_at);
        }
    },

    /**-------------------------------------------------------------------------
     */
    getRewards() {
        return this.rewards;
    },

    /**-------------------------------------------------------------------------
     */
    setRewards(rewards) {
        if (!_.isEqual(rewards, this.rewards)) {
            this.rewards = rewards;
            this.trigger('updateRewards', this.rewards);
        }
    },

    /**-------------------------------------------------------------------------
     */
    getLoyalty(scheme) {
        if (scheme) {
            return this.loyalty[scheme] || null;
        }
        return this.loyalty;
    },

    /**-------------------------------------------------------------------------
     */
    setLoyalty(loyalty) {
        if (!_.isEqual(loyalty, this.loyalty)) {
            this.loyalty = loyalty;
            this.trigger('updateLoyalty', this.loyalty);
        }
    },

    /**-------------------------------------------------------------------------
     */
    getCreditBalance() {
        return this.credit_balance;
    },

    /**-------------------------------------------------------------------------
     */
    setCreditBalance(credit_balance) {
        if (!this.credit_balance.isEqual(credit_balance)) {
            this.credit_balance.set(credit_balance);
            this.trigger('updateCreditBalance', this.credit_balance);
        }
    },

    /**-------------------------------------------------------------------------
     * Note: this is not a long-term implementation
     */
    getActiveLoyaltyProgram() {
        if (this.loyalty.LSNav1) {
            return 'LSNav1';
        }

        if (this.loyalty.PunchCard) {
            return 'PunchCard';
        }

        if (this.loyalty.Shift8Points) {
            return 'Shift8Points';
        }

        return null;
    },

    getActiveLoyaltyPrograms() {
        return _.keys(this.loyalty);
    },

    getDateOfBirth() {
        return this.date_of_birth;
    },

    setDateOfBirth(date_of_birth) {
        if (date_of_birth) {
            if (moment.isMoment(date_of_birth)) {
                date_of_birth = date_of_birth.clone();
            } else {
                date_of_birth = moment.utc(date_of_birth);
            }
        }
        if (
            date_of_birth &&
            this.date_of_birth &&
            date_of_birth.isSame(this.date_of_birth, 'day')
        ) {
            return;
        }
        this.date_of_birth = date_of_birth;
        this.trigger('updateDateOfBirth', this.date_of_birth);
    },

    getGender() {
        return this.gender;
    },

    setGender(gender) {
        if (gender !== this.gender) {
            this.gender = gender;
            this.trigger('updateGender', this.gender);
        }
    },

    getDietaryPreference() {
        return this.dietary_preference || [];
    },

    setDietaryPreference(dietary_preference) {
        if (!dietary_preference || !_.isArray(dietary_preference)) {
            dietary_preference = [];
        }
        if (_.isEqual(dietary_preference, this.dietary_preference)) {
            return;
        }
        this.dietary_preference = _.clone(dietary_preference);
        this.trigger('updateDietaryPreference', this.dietary_preference);
    },

    getFavouriteLocation() {
        return this.favourite_location
            ? Mobi2Go.Locations.getById(this.favourite_location.id)
            : null;
    },

    setFavouriteLocation(favourite_location) {
        if (favourite_location) {
            if (!(favourite_location instanceof Mobi2Go._class.Location)) {
                favourite_location = Mobi2Go.Locations.getById(
                    favourite_location.id
                );
                if (!favourite_location) {
                    return;
                }
            }
        }
        if (
            (!favourite_location && !this.favourite_location) ||
            (favourite_location &&
                this.favourite_location &&
                favourite_location.id === this.favourite_location.id)
        ) {
            return;
        }
        this.favourite_location = favourite_location
            ? { id: favourite_location.id }
            : null;
        this.trigger('updateFavouriteLocation', this.getFavouriteLocation());
    },

    getPostCode() {
        return this.post_code;
    },

    setPostCode(post_code) {
        if (post_code !== this.post_code) {
            this.post_code = post_code;
            this.trigger('updatePostCode', this.post_code);
        }
    },

    getLanguage() {
        return this.language;
    },

    setLanguage(language) {
        if (language !== this.language) {
            this.language = language;
            this.trigger('updateLanguage', this.language);
        }
    },

    /**-------------------------------------------------------------------------
     */
    providePassword(pw, callback) {
        const that = this;
        password_to_verify = pw;

        clearTimeout(this.providePassword.timeout);
        this.providePassword.timeout = setTimeout(function() {
            password_to_verify = null;
            that.isPasswordProvided.is_valid = false;
        }, VALID_PASSWORD_TTL);

        this.isPasswordProvided.is_valid = false;
        this.validatePassword(callback);
    },

    /**-------------------------------------------------------------------------
     */
    validatePassword(callback) {
        const that = this;
        if (!_.isFunction(callback)) {
            callback = function() {};
        }

        if (password_to_verify === null) {
            _.defer(function() {
                callback(false);
            });

            return;
        }
        if (this.isPasswordProvided.is_valid) {
            _.defer(function() {
                callback(true);
            });

            return;
        }

        serverRequest({
            api_version: 1,
            endpoint: ['customers', this.email, 'password', 'verify'],
            type: 'POST',
            data: { password: password_to_verify },
            complete(response) {
                const is_valid = response.status === 200;
                that.isPasswordProvided.is_valid = is_valid;
                callback(is_valid);
            },
            error() {
                return true; // disable default UI alerts because we expect a 401
            },
        });
    },

    /**-------------------------------------------------------------------------
     */
    provideFacebookAuthentication(authorisation, callback) {
        this.validateFacebookAuthentication(authorisation, callback);
    },

    /**-------------------------------------------------------------------------
     */
    validateFacebookAuthentication(authorisation, callback) {
        const that = this;
        if (!_.isFunction(callback)) {
            callback = function() {};
        }

        if (authorisation === null) {
            _.defer(function() {
                callback(false);
            });
            return;
        }

        if (authorisation.email !== email) {
            _.defer(function() {
                callback(false);
            });
            return;
        }

        serverRequest({
            api_version: 1,
            endpoint: ['customers', 'sso', 'facebook', 'verify'],
            type: 'POST',
            data: authorisation,
            complete(response) {
                const is_valid = response.status === 200;
                that.isPasswordProvided.is_valid = is_valid;
                callback(is_valid);
            },
            error() {
                return true; // disable default UI alerts because we expect a 401
            },
        });
    },

    /**-------------------------------------------------------------------------
     */
    provideAppleAuthentication(authorisation, callback) {
        this.validateAppleAuthentication(authorisation, callback);
    },

    /**-------------------------------------------------------------------------
     */
    validateAppleAuthentication(authorisation, callback) {
        const that = this;
        if (!_.isFunction(callback)) {
            callback = function() {};
        }

        if (authorisation === null) {
            _.defer(function() {
                callback(false);
            });

            return;
        }

        serverRequest({
            api_version: 1,
            endpoint: ['customers', 'sso', 'apple', 'verify'],
            type: 'POST',
            data: authorisation,
            complete(response) {
                const is_valid = response.status === 200;
                that.isPasswordProvided.is_valid = is_valid;
                callback(is_valid);
            },
            error() {
                return true; // disable default UI alerts because we expect a 401
            },
        });
    },

    /**-------------------------------------------------------------------------
     */
    isPasswordProvided() {
        return !!this.isPasswordProvided.is_valid;
    },

    /**-------------------------------------------------------------------------
     */
    update(properties, callback) {
        properties = _.pick(
            properties,
            'name',
            'phone_number',
            'loyalty_id',
            'is_subscriber',
            'date_of_birth',
            'gender',
            'dietary_preference',
            'favourite_location',
            'post_code',
            'language'
        );

        this.set(properties).save(callback);

        return this;
    },

    /**-------------------------------------------------------------------------
     */
    updatePassword(password, new_password, callback) {
        const that = this;
        const handleFailure = function() {
            that.trigger('updatePasswordFail');
            _.isFunction(callback) && callback('error');

            return true;
        };
        serverRequest({
            api_version: 1,
            endpoint: ['customers', this.email, 'password'],
            type: 'PUT',
            data: {
                current_password: password,
                new_password,
            },
            statusCode: {
                400: handleFailure,
                401: handleFailure,
            },
            success(response) {
                if (response && response.message === 'password updated') {
                    password_to_verify = new_password;
                    that.isPasswordProvided.is_valid = true;
                    that.trigger('updatePassword');
                    _.isFunction(callback) && callback('success');
                } else {
                    handleFailure();
                }
            },
        });

        return this;
    },

    /**-------------------------------------------------------------------------
     */
    updateEmail(new_email, password, callback) {
        const that = this;
        function handleFailure(reason) {
            return function() {
                that.trigger('updateEmailFail', reason);
                _.isFunction(callback) && callback(reason);
            };
        }

        Mobi2Go.Customer.isEmailRegistered(new_email, function(
            is_email_registered
        ) {
            if (is_email_registered) {
                handleFailure('email in use')();
                return;
            }

            serverRequest({
                api_version: 1,
                endpoint: ['customers', that.email, 'email'],
                type: 'PUT',
                data: {
                    email: new_email,
                    password,
                },
                statusCode: {
                    401: handleFailure('incorrect password'),
                    400: handleFailure('email in use'),
                    200() {
                        that.email = new_email;
                        that.trigger('updateEmail', new_email);
                        _.isFunction(callback) && callback('success');
                    },
                },
            });
        });

        return this;
    },

    /**-------------------------------------------------------------------------
     */
    save(callback) {
        const that = this;
        serverRequest({
            api_version: 1,
            endpoint: ['customers', this.email],
            type: 'PUT',
            data: {
                name: this.name,
                phone_number: this.phone_number,
                loyalty_id: this.loyalty_id,
                is_subscriber: this.is_subscriber,
                addresses: this.addresses.invokeMap('get'),
                date_of_birth: this.date_of_birth,
                gender: this.gender,
                dietary_preference: this.dietary_preference,
                favourite_location: this.favourite_location,
                post_code: this.post_code,
                language: this.language,
            },
            success(response) {
                that.set(response);
                that.trigger('update');
                _.isFunction(callback) && callback('success');
            },
            error(error) {
                try {
                    const parsed_error = JSON.parse(error.responseText);
                    if (parsed_error.error) {
                        that.trigger('updateError', parsed_error.error);
                        _.isFunction(callback) &&
                            callback('error', parsed_error.error);
                        // Mark error as handled to prevent
                        // generic error UI from triggering
                        return true;
                    }
                } catch (e) {}
            },
        });

        return this;
    },

    /**-------------------------------------------------------------------------
     */
    refresh(callback) {
        const that = this;
        serverRequest({
            api_version: 1,
            endpoint: ['customers', this.email],
            type: 'GET',
            success(response) {
                that.set(response);
                that.trigger('update');
                _.isFunction(callback) && callback('success');
            },
        });

        return this;
    },

    /**-------------------------------------------------------------------------
     */
    getAddresses() {
        return this.addresses;
    },

    /**-------------------------------------------------------------------------
     */
    getAddressById(id) {
        return this.addresses.getById(id);
    },

    /**-------------------------------------------------------------------------
     */
    setAddresses(addresses) {
        addresses = _.map(addresses, properties => {
            return Mobi2Go._class.CustomerAddress(properties, this);
        });

        this.addresses.set(addresses);
        this.trigger('updateAddresses', this.getAddresses());

        return this;
    },

    /**-------------------------------------------------------------------------
     */
    createAddress(properties, callback) {
        const address = Mobi2Go._class.CustomerAddress(
            properties,
            this,
            callback
        );
        this.addresses.push(address);
        this.trigger('createAddress', address);
        this.trigger('updateAddresses', this.getAddresses());

        return address;
    },

    /**-------------------------------------------------------------------------
     */
    updateAddressById(id, properties, callback) {
        const that = this;
        const address = this.getAddressById(id);
        if (!address) {
            throw new Error(`cannot update, unknown address id #${id}`);
        }
        return address.set(properties).save(function() {
            _.isFunction(callback) && callback(address);
            that.trigger('updateAddresses', that.getAddresses());
        });
    },

    /**-------------------------------------------------------------------------
     */
    removeAddressById(id, callback) {
        const address = this.getAddressById(id);
        if (!address) {
            throw new Error(`cannot remove, unknown address id #${id}`);
        }
        this.removeAddress(address, callback);
    },

    /**-------------------------------------------------------------------------
     */
    removeAddress(address, callback) {
        if (!address.id) {
            return;
        }

        const that = this;
        const { id } = address;

        this.addresses.set(this.addresses.reject({ id: address.id }));

        this.trigger('removeAddress', address);
        this.trigger('updateAddresses', that.getAddresses());

        // So optimistic
        serverRequest({
            api_version: 1,
            endpoint: ['customers', this.email, 'addresses', id],
            type: 'DELETE',
            success(response) {
                if (_.isFunction(callback)) {
                    callback(that.getAddresses());
                }
            },
        });
    },

    /**-------------------------------------------------------------------------
     */
    removePaymentCardToken(token, callback) {
        return this.removePaymentCardTokenById(token.id, callback);
    },

    /**-------------------------------------------------------------------------
     */
    removePaymentCardTokenById(id, callback) {
        serverRequest({
            api_version: 1,
            endpoint: ['customers', this.email, 'payment_card_tokens', id],
            type: 'DELETE',
            success: callback,
        });
    },

    shouldPromptSMSVerification() {
        return (
            Mobi2Go.headoffice.customer_phone_number_sms_verification_enabled &&
            !this.getVerifiedAt()
        );
    },

    /**-------------------------------------------------------------------------
     */
    shouldPromptToJoinLoyaltyClub() {
        return (
            !this.shouldPromptSMSVerification() &&
            Mobi2Go.headoffice.customer_loyalty_club_enabled &&
            !this.isInLoyaltyClub() &&
            !!this.phone_number
        );
    },

    /**-------------------------------------------------------------------------
     */
    isInLoyaltyClub() {
        return this.date_of_birth !== null;
    },

    /**-------------------------------------------------------------------------
     */
    getOrders(query, callback) {
        serverRequest({
            api_version: 1,
            endpoint: ['customers', this.email, 'orders'],
            type: 'GET',
            data: query,
            success: callback,
        });

        return this;
    },

    getGiftCard(success, error) {
        const program = Mobi2Go.headoffice.credit_program.scheme_id;
        serverRequest({
            api_version: 1,
            endpoint: [
                'customers',
                this.email,
                'credit',
                program.toLowerCase(),
            ],
            type: 'GET',
            success,
            error,
        });
    },

    addGiftCard(query, success, error) {
        const program = Mobi2Go.headoffice.credit_program.scheme_id;
        serverRequest({
            api_version: 1,
            endpoint: [
                'customers',
                this.email,
                'credit',
                program.toLowerCase(),
            ],
            type: 'POST',
            data: query,
            success,
            error,
        });
    },

    removeGiftCard(success, error) {
        const program = Mobi2Go.headoffice.credit_program.scheme_id;
        serverRequest({
            api_version: 1,
            endpoint: [
                'customers',
                this.email,
                'credit',
                program.toLowerCase(),
            ],
            type: 'DELETE',
            success,
            error,
        });
    },

    requestVerification() {
        return new Promise((resolve, reject) => {
            serverRequest({
                api_version: 1,
                endpoint: ['customers', this.email, 'verification'],
                type: 'POST',
                success: resolve,
                error: reject,
            });
        });
    },

    completeVerification(code) {
        return new Promise((resolve, reject) => {
            serverRequest({
                api_version: 1,
                endpoint: ['customers', this.email, 'verification'],
                type: 'PUT',
                data: {
                    code,
                },
                success: () => {
                    this.refresh(() => {
                        resolve();
                    });
                },
                error: () => {
                    reject();
                    return true;
                },
            });
        });
    },
});

/**-----------------------------------------------------------------------------
 */

function sanitiseName(string) {
    string += '';
    return string.replace(/[^A-Za-z0-9-&()'.\s]/g, '').substring(0, 50);
}

function sanitiseNumber(string) {
    string += '';
    return string.replace(/[^A-Za-z0-9-\s]/g, '').substring(0, 50);
}
